import React, { useState, useEffect } from 'react';
import './singupNewCap.css';
import {
  FormGroup,
  FormControl,
  ControlLabel,
  Button,
  Collapse,
} from 'react-bootstrap';
import LoaderButton from '../components/LoaderButton';
import { getCaps, createNeighborhood, activateCapByKey } from '../utils/bedrockAPI';
import { setNewCapSignup } from '../utils/Common';

export default function SignupNewCap(props) {
  const [capCode, setCapCode] = useState('');
  const [capName, setName] = useState('');
  const [neighborhoodSelection, setNeighborhoodSelection] = useState('Do not assign a Neighborhood');
  const [newNeighborhood, setNewNeighborhood] = useState('');
  const [neighborhoods, setNeighborhoods] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [caps, setCaps] = useState(null);

  const [errorCollapse, setErrorCollapse] = useState('');
  const [capCodeInfo, setCapCodeInfo] = useState(false);
  const [capNameInfo, setCapNameInfo] = useState(false);
  const [capNeighborhoodInfo, setCapNeighborhoodInfo] = useState(false);

  useEffect(() => {
    async function loadCaps() {
      const tempCaps = await getCaps();
      setCaps(tempCaps);
    }
    loadCaps();

    if (props.match && props.match.params.capId) {
      const capId = props.match.params.capId;
      const activationCode = capId.substring(0, 8);
      setCapCode(activationCode);
    }
  }, [props.match]);

  useEffect(() => {
    const compileNeighborhoods = (capList) => {
      const list = [];
      list.push({ null: 'Do not assign a Neighborhood' });
      list.push({ new: 'Create New Neighborhood' });
      for (let i = 0; i < capList.length; i++) {
        if (capList[i].neighborhoodid) {
          list.push({ [capList[i].neighborhoodid]: capList[i].neighborhood });
        }
      }
      return list;
    };
    if (caps) {
      setNeighborhoods(compileNeighborhoods(caps));
    }
  }, [caps]);

  function validateForm() {
    return (
      /^[0-9a-fA-F]{8}$/.test(capCode)
      && capName.length > 0
      && (neighborhoodSelection === 'Create New Neighborhood'
        ? newNeighborhood && newNeighborhood.length > 0
        : true)
    );
  }

  function lookUpNeighborhoodId(neighborhoodName) {
    for (let i = 0; i < neighborhoods.length; i++) {
      if (Object.values(neighborhoods[i])[0] === neighborhoodName) {
        return Object.keys(neighborhoods[i])[0];
      }
    }
    return null;
  }

  async function handleSignup(event) {
    event.preventDefault();

    setIsLoading(true);
    let neighborhoodId;
    if (neighborhoodSelection === 'Create New Neighborhood') {
      neighborhoodId = (await createNeighborhood(newNeighborhood)).neighborhoodId;
    } else if (!neighborhoodSelection || neighborhoodSelection === 'Do not assign a Neighborhood') {
      neighborhoodId = null;
    } else {
      neighborhoodId = lookUpNeighborhoodId(neighborhoodSelection);
      if (neighborhoodId === 'null' || neighborhoodId === 'new') {
        neighborhoodId = null;
      }
    }

    const capActivation = {
      name: capName,
      key: capCode,
      ...(neighborhoodId ? { neighborhood: neighborhoodId } : {}),
    };

    try {
      await activateCapByKey(capActivation);
      setNewCapSignup(false);
      props.history.push('/dashboard');
    } catch (error) {
      if (error.response?.status === 429) {
        setErrorCollapse('Too many attempts. Please wait a few minutes and try again.');
      } else if (error.response?.status === 422) {
        setErrorCollapse('Please recheck your activation code');
      } else if (error.response?.status === 401) {
        setErrorCollapse('Please contact the Owner of this unit for access');
      }
    }

    setIsLoading(false);
  }

  return (
    <div className="signup-new-cap-page">
      <header className="signup-new-cap-header">
        <h1>Welcome to Bedrock Wireless</h1>
        <p className="signup-new-cap-subtitle">
          You have received a Construction Access Point (CAP). Make sure your CAP is plugged in
          and turned on, then complete the form below to connect it to your account.
        </p>
      </header>

      <div className="signup-new-cap-panel">
        <div className="signup-new-cap-instructions">
          <p>
            Locate the 8-character code on the inside lid of your CAP (4cam) or the back of the
            unit (2cam) and enter it below.
          </p>
          <p>
            Provide a name for your CAP — typically a street address (e.g. 123 Main St). You may
            also assign an existing or new neighborhood to organize your sites.
          </p>
        </div>

        <form className="signup-new-cap-form" onSubmit={handleSignup}>
          <FormGroup controlId="capCode">
            <div className="signup-field-header">
              <ControlLabel>Activation Code</ControlLabel>
              <Button
                className="btn-brand-outline btn-compact signup-info-btn"
                onClick={() => setCapCodeInfo(!capCodeInfo)}
                type="button"
              >
                <i className="fas fa-info-circle" />
              </Button>
            </div>
            <Collapse in={capCodeInfo}>
              <div className="signup-help-box">
                Lower or upper case does not matter. Please make sure you have 8 characters.
              </div>
            </Collapse>
            <FormControl
              autoFocus
              type="text"
              value={capCode}
              maxLength={8}
              onChange={e => setCapCode(e.target.value)}
            />
          </FormGroup>

          <FormGroup controlId="capName">
            <div className="signup-field-header">
              <ControlLabel>CAP Name</ControlLabel>
              <Button
                className="btn-brand-outline btn-compact signup-info-btn"
                onClick={() => setCapNameInfo(!capNameInfo)}
                type="button"
              >
                <i className="fas fa-info-circle" />
              </Button>
            </div>
            <Collapse in={capNameInfo}>
              <div className="signup-help-box">
                A name can be anything you want. Typically the address of the jobsite is used
                (e.g. 123 Main St). You can edit this later.
              </div>
            </Collapse>
            <FormControl
              type="text"
              value={capName}
              onChange={e => setName(e.target.value)}
            />
          </FormGroup>

          <FormGroup controlId="neighborhood">
            <div className="signup-field-header">
              <ControlLabel>Neighborhood</ControlLabel>
              <Button
                className="btn-brand-outline btn-compact signup-info-btn"
                onClick={() => setCapNeighborhoodInfo(!capNeighborhoodInfo)}
                type="button"
              >
                <i className="fas fa-info-circle" />
              </Button>
            </div>
            <Collapse in={capNeighborhoodInfo}>
              <div className="signup-help-box">
                Neighborhoods help organize your CAPs. They are optional — use a job name,
                customer name, or any label that helps you find a site quickly.
              </div>
            </Collapse>
            <FormControl
              componentClass="select"
              value={neighborhoodSelection}
              onChange={e => setNeighborhoodSelection(e.target.value)}
            >
              {neighborhoods && neighborhoods.map((nh, i) => (
                <option key={i}>
                  {nh[Object.keys(nh)[0]]}
                </option>
              ))}
            </FormControl>
          </FormGroup>

          {neighborhoodSelection === 'Create New Neighborhood' && (
            <FormGroup controlId="newNeighborhood">
              <ControlLabel>New neighborhood name</ControlLabel>
              <FormControl
                type="text"
                value={newNeighborhood}
                onChange={e => setNewNeighborhood(e.target.value)}
              />
            </FormGroup>
          )}

          <div className="signup-new-cap-actions">
            <LoaderButton
              type="submit"
              className="btn-brand-dark btn-compact"
              isLoading={isLoading}
              disabled={!validateForm()}
            >
              Launch CAP
            </LoaderButton>
          </div>

          <Collapse in={errorCollapse.length > 0}>
            <div className="signup-error-box">
              {errorCollapse}
            </div>
          </Collapse>
        </form>
      </div>
    </div>
  );
}
